import * as React from 'react'

import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file

import useI18n from '@store/i18n/useI18n'
import Theme from '@theme/Theme'
import styled from '@theme/styled-components'

import { DateRange } from 'react-date-range'
import { fr } from 'date-fns/locale'
import setHours from 'date-fns/esm/setHours/index'

interface Props {
  startDate: Date | undefined
  setStartDate: (date: Date | undefined) => void
  endDate: Date | undefined
  setEndDate: (date: Date | undefined) => void
  placeholder?: string
}

const Picker = ({ startDate, setStartDate, endDate, setEndDate, placeholder }: Props) => {
  const i18n = useI18n()

  const [pickerOpened, setPickerOpened] = React.useState<boolean>(false)

  const handleSelect = (ranges: any) => {
    setStartDate(setHours(ranges.selection.startDate, 12))
    setEndDate(setHours(ranges.selection.endDate, 12))
  }

  return (
    <>
      <Container pickerOpened={pickerOpened}>
        <ValueContainer
          onClick={() => {
            setPickerOpened(!pickerOpened)
            if (!startDate) {
              setStartDate(new Date())
              setEndDate(new Date())
            }
          }}>
          <Value>
            {startDate && endDate
              ? i18n.t('components.calendar.rangeDate', { startDate: new Date(startDate), endDate: new Date(endDate) })
              : placeholder || i18n.t('components.calendar.placeholder')}
          </Value>
        </ValueContainer>
        {!!startDate && (
          <CrossContainer
            onClick={() => {
              setStartDate(undefined)
              setEndDate(undefined)
              setPickerOpened(false)
            }}>
            <svg
              height="20"
              width="20"
              viewBox="0 0 20 20"
              aria-hidden="true"
              focusable="false"
              fill={Theme.colors.filterPrimarycolor}>
              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
            </svg>
          </CrossContainer>
        )}
      </Container>
      {pickerOpened && (
        <>
          <CalendarContainer>
            <DateRange
              locale={fr}
              ranges={[
                {
                  startDate,
                  endDate,
                  key: 'selection',
                },
              ]}
              onChange={handleSelect}
              rangeColors={[Theme.colors.filterPrimarycolor]}
            />
          </CalendarContainer>
          <Background onClick={() => setPickerOpened(false)} />
        </>
      )}
    </>
  )
}

export default Picker

const CalendarContainer = styled('div')`
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  position: relative;
  display: flex;
  z-index: 2;
  top: 0;

  font-family: Avenir, -webkit-pictograph, sans-serif;

  .rdrDateDisplayWrapper {
    display: none;
  }
  .rdrMonthAndYearWrapper {
    margin: 0 20px;
    padding: 0;
  }
  .rdrCalendarWrapper {
    border-radius: 5px;
  }
  .rdrNextPrevButton {
    background-color: ${(props) => props.theme.colors.filterSecondaryColor};
  }
  .rdrNextButton i {
    border-color: transparent transparent transparent ${(props) => props.theme.colors.filterPrimarycolor};
  }
  .rdrPprevButton i {
    border-color: transparent ${(props) => props.theme.colors.filterPrimarycolor} transparent transparent;
  }
  .rdrMonthPicker select,
  .rdrYearPicker select {
    ${(props) => props.theme.fonts.calendarLibelle};
    display: flex;
    align-items: center;
    text-align: center;
    text-transform: capitalize;
    text-align-last: center;
  }
  .rdrWeekDay {
    ${(props) => props.theme.fonts.calendarLibelle01}
    text-transform: capitalize;
  }
  .rdrDayNumber span {
    ${(props) => props.theme.fonts.calendarLibelle02}

    display: flex;
    align-items: center;
    text-align: center;
  }

  .rdrDayPassive span {
    ${(props) => props.theme.fonts.calendarLibelle03}

    display: flex;
    align-items: center;
    text-align: center;
  }
`
const Background = styled('div')`
  background-color: ${(props) => props.theme.colors.backgroundColor};
  position: absolute;
  cursor: pointer;
  z-index: 1;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
`
const Container = styled('div')<{ pickerOpened: boolean }>`
  margin: 5px 20px;
  ${(props) => props.pickerOpened && 'margin-bottom: 10px; z-index: 2;'}
  background: ${(props) => props.theme.colors.filterSecondaryColor};
  border: 1px solid ${(props) => props.theme.colors.searchbarBorder};
  box-sizing: border-box;
  border-radius: 7px;
  height: 50px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  cursor: pointer;
  justify-content: space-between;
`
const Value = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle04}
  margin: 0 2px;
`
const CrossContainer = styled('div')`
  display: flex;
  height: 50px;
  width: 20px;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
`
const ValueContainer = styled('div')`
  flex: 1;
  height: 50px;
  display: flex;
  align-items: center;
`
